import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Page } from "../../../common/components/Page";
import {
  useMyModerationStatus,
  useSubmitBanAppeal,
} from "../hooks/useModerationRecovery";
import { BACK_TO_ACCOUNT, useAccountActive } from "../hooks/useAccountBack";

type SelfAppeal = NonNullable<
  NonNullable<ReturnType<typeof useMyModerationStatus>["data"]>["appeal"]
>;

function formatDateTime(value: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC",
    }).format(new Date(value));
  } catch {
    return "the time shown by the server";
  }
}

function appealTime(appeal: SelfAppeal): number {
  const value = Date.parse(appeal.created_at);
  return Number.isFinite(value) ? value : 0;
}

function selectVisibleAppeal(
  canonicalAppeal: SelfAppeal | null | undefined,
  mutationAppeal: SelfAppeal | null | undefined,
  activeBanId: string | null,
): SelfAppeal | null {
  const eligibleMutation =
    mutationAppeal?.ban_id === activeBanId ? mutationAppeal : null;
  if (!canonicalAppeal) return eligibleMutation;
  if (!eligibleMutation) return canonicalAppeal;
  if (canonicalAppeal.appeal_id === eligibleMutation.appeal_id) {
    return canonicalAppeal;
  }
  return appealTime(eligibleMutation) >= appealTime(canonicalAppeal)
    ? eligibleMutation
    : canonicalAppeal;
}

/**
 * Authenticated recovery surface deliberately mounted outside RequireProfile
 * and AppShell. A restricted player keeps access to reads, legal deletion, and
 * the one ban-bound appeal path without receiving operator-only details.
 */
export function ModerationRecoveryPage() {
  const status = useMyModerationStatus();
  const active = useAccountActive();

  return (
    <Page title="Safety & appeals" narrow back={active ? BACK_TO_ACCOUNT : undefined}>
      {status.isLoading ? (
        <Typography
          color="text.secondary"
          role="status"
          aria-label="Loading safety and appeal status"
        >
          Loading your safety and appeal status…
        </Typography>
      ) : status.isError || !status.data ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void status.refetch()}
            >
              Try again
            </Button>
          }
        >
          We couldn&apos;t load your safety status. It may be temporarily
          unavailable.
        </Alert>
      ) : (
        <ModerationStatus status={status.data} />
      )}
      <Typography variant="body2" color="text.secondary">
        Need more help? Contact support without sharing private account or
        moderation identifiers.
      </Typography>
    </Page>
  );
}

function ModerationStatus({
  status,
}: {
  status: NonNullable<ReturnType<typeof useMyModerationStatus>["data"]>;
}) {
  const submitAppeal = useSubmitBanAppeal();
  const [appealOpen, setAppealOpen] = useState(false);
  const [reason, setReason] = useState("");
  const keyRef = useRef<string | null>(null);
  const requestReasonRef = useRef<string | null>(null);
  const commandBanRef = useRef<string | null>(null);
  const submittingRef = useRef(false);
  const activeBan = status.active_ban;
  const mutationAppeal = submitAppeal.data?.appeal;
  const appeal = selectVisibleAppeal(
    status.appeal,
    mutationAppeal,
    activeBan?.ban_id ?? null,
  );

  useEffect(() => {
    const canonicalAppeal = status.appeal;
    const canonicalResolvedCurrentCommand =
      canonicalAppeal &&
      canonicalAppeal.status !== "pending" &&
      (!mutationAppeal ||
        canonicalAppeal.appeal_id === mutationAppeal.appeal_id ||
        appealTime(canonicalAppeal) >= appealTime(mutationAppeal));
    if (canonicalResolvedCurrentCommand) {
      keyRef.current = null;
      requestReasonRef.current = null;
      commandBanRef.current = null;
    }
  }, [mutationAppeal, status.appeal]);

  const hasActiveRestriction = status.is_banned && activeBan !== null;
  const appealCopy = appeal
    ? appeal.status === "pending"
      ? hasActiveRestriction
        ? "Your appeal is pending and under review."
        : "No active restriction currently applies. Your appeal remains pending."
      : appeal.status === "upheld"
        ? hasActiveRestriction
          ? "Your appeal was upheld. The restriction remains in place."
          : "No active restriction currently applies. Your last appeal was upheld."
        : appeal.status === "lifted"
          ? "Your appeal was lifted. The restriction has been removed."
          : "Your appeal closed because the restriction was already inactive."
    : null;
  const appealStatusPanel =
    appeal && appealCopy ? (
      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1}>
          <Typography component="h2" variant="h6">
            Appeal status
          </Typography>
          <Alert
            severity={
              appeal.status === "lifted" || appeal.status === "inactive"
                ? "success"
                : "info"
            }
          >
            {appealCopy}
          </Alert>
        </Stack>
      </Paper>
    ) : null;

  if (
    appeal &&
    (!hasActiveRestriction ||
      !activeBan ||
      !activeBan.can_appeal ||
      appeal.status === "pending")
  ) {
    return appealStatusPanel;
  }

  if (!status.is_banned || !activeBan) {
    return (
      appealStatusPanel ?? (
        <Alert severity="success">
          No active restriction applies to your account.
        </Alert>
      )
    );
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed || trimmed.length > 2000 || submittingRef.current) return;
    if (commandBanRef.current !== activeBan.ban_id) {
      keyRef.current = null;
      requestReasonRef.current = null;
      commandBanRef.current = activeBan.ban_id;
    }
    keyRef.current ??= globalThis.crypto.randomUUID();
    requestReasonRef.current ??= trimmed;
    submittingRef.current = true;
    submitAppeal.mutate(
      {
        banId: activeBan.ban_id,
        idempotencyKey: keyRef.current,
        reason: requestReasonRef.current,
      },
      {
        onSettled: () => {
          submittingRef.current = false;
        },
        onSuccess: () => setAppealOpen(false),
      },
    );
  };

  const restrictionPanel = (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Typography component="h2" variant="h6">
          Account restriction
        </Typography>
        {activeBan.expires_at ? (
          <Alert severity="warning" role="note">
            This account has a temporary interaction restriction. It expires at{" "}
            <time dateTime={activeBan.expires_at}>
              {formatDateTime(activeBan.expires_at)} UTC
            </time>
            . Public and account-recovery reads remain available.
          </Alert>
        ) : (
          <Alert severity="warning" role="note">
            This account has a permanent interaction restriction. Public and
            account-recovery reads remain available.
          </Alert>
        )}

        {activeBan.can_appeal && !appealOpen ? (
          <Button
            variant="contained"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => {
              if (commandBanRef.current !== activeBan.ban_id) {
                keyRef.current = null;
                requestReasonRef.current = null;
                commandBanRef.current = activeBan.ban_id;
              }
              keyRef.current ??= globalThis.crypto.randomUUID();
              setAppealOpen(true);
            }}
          >
            Appeal this restriction
          </Button>
        ) : null}

        {activeBan.can_appeal && appealOpen ? (
          <Stack component="form" spacing={1.5} onSubmit={submit}>
            <Typography color="text.secondary">
              Explain why this restriction should be reviewed. Do not include
              passwords, payment details, or private identifiers.
            </Typography>
            <TextField
              label="Reason"
              multiline
              minRows={4}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              disabled={
                submitAppeal.isPending ||
                (submitAppeal.isError &&
                  commandBanRef.current === activeBan.ban_id &&
                  requestReasonRef.current !== null)
              }
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {submitAppeal.isError ? (
              <Alert severity="error" role="alert">
                We couldn&apos;t submit your appeal. Try again with the same
                request.
              </Alert>
            ) : null}
            <Stack direction="row" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                disabled={!reason.trim() || submitAppeal.isPending}
              >
                {submitAppeal.isPending ? "Submitting…" : "Submit appeal"}
              </Button>
              <Button
                type="button"
                color="inherit"
                disabled={submitAppeal.isPending}
                onClick={() => setAppealOpen(false)}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );

  return appealStatusPanel ? (
    <Stack spacing={2}>
      {appealStatusPanel}
      {restrictionPanel}
    </Stack>
  ) : (
    restrictionPanel
  );
}
